/* import __COLOCATED_TEMPLATE__ from './project-completion-line-item-selector.hbs'; */
import { notEmpty } from '@ember/object/computed';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import Component from '@ember/component';
import { guidFor } from '@ember/object/internals';
import { PROJECT_LINE_ITEM } from "../../models/project-line-item";
import { makeMoney } from "../../helpers/make-money";

const CLASS_NAME = 'SH-c-project-completion-line-item-selector';
const REFRESH_DEBOUNCE_MS = 750;

/**
 * This is the project information section of the project completion flow
 */
export default Component.extend({
  /** @inheritDoc */
  tagName: 'div',
  classNames: [CLASS_NAME],
  CLASS_NAME,

  /*********************************
   * REQUIRED PARAMS
   *********************************/

  /** @param {Function} handleSubmit Handler function for form submission. */
  handleRefresh: () => {},

  errors: undefined,

  /*********************************
   * OPTIONAL PARAMS
   **********************************/

  /*********************************
   * LIFECYCLE FUNCTIONS
   **********************************/

  /*********************************
   * PRIVATE
   **********************************/

  /** @type {boolean} */
  submitting: false,

  /** @inheritDoc */
  init() {
    this._super(...arguments);
    this.set('inlineId', guidFor(this));
  },

  /**
   * Register services
   */
  state: service('states/routes/project-completion'),
  stateManager: service('state-managers/routes/project-completion'),

  /*********************************
   * REQUIRED PARAMS
   *********************************/

  /*********************************
   * OPTIONAL PARAMS
   **********************************/

  /*********************************
   * LIFECYCLE FUNCTIONS
   **********************************/

  /**
   * Update our model based on changes to the base amount selection
   *
   * @private
   */
  _updateBaseAmount: task(function *() {
    yield timeout(REFRESH_DEBOUNCE_MS);

    // Force base to be selected if we have no expenses
    if (!this.get('_showBaseCheckbox')) {
      this.set('state.advanceBaseSelected', true);
    }

    const currentAmount = this.get('state.testBillingRecord.baseAmount') || 0.0;
    const currentBaseDescription = this.get('state.testBillingRecord.baseDescription') || '';

    const baseSelected = this.get('state.advanceBaseSelected');
    const baseAmount = (baseSelected && this.get('state.advanceBaseAmount')) ? this.get('state.advanceBaseAmount') : 0.0;
    const baseDescription = this.get('state.advanceBaseDescription');

    if (
      makeMoney([currentAmount]) !== makeMoney([baseAmount])
      || baseDescription !== currentBaseDescription
    ) {
      this.get('handleRefresh')();
    }
  }).restartable(),

  /*********************************
   * PRIVATE
   *********************************

   /** @type {Object} */
  _selectedExpenses: computed(
    '_expenseProjectLineItems.@each.id',
    'state.advanceProjectLineItemIds.[]',
    function () {
      const selectedExpenses = {};

      // Existing billable
      this.get('_expenseProjectLineItems').forEach((projectLineItem) => {
        selectedExpenses[projectLineItem.id] = this.get('state.advanceProjectLineItemIds').includes(projectLineItem.id);
      });

      return selectedExpenses;
    }
  ),

  /**
   * Return all ProjectLineItems that have either been included on the test billable, or have not yet been paid for
   */
  _expenseProjectLineItems: computed(
    'state.currentUser.isBuyer',
    'state.application.projectLineItems.@each.{amount,invoiceFinalAmount,paymentFinalAmount,type}',
    function () {
      return this.get('state.application.projectLineItems').filter(projectLineItem => {
        if (projectLineItem.get('type') !== PROJECT_LINE_ITEM.TYPES.EXPENSE) {
          return false;
        }

        if (this.get('state.currentUser.isBuyer')) {
          return projectLineItem.get('invoiceFinalAmount') < projectLineItem.get('amount');
        } else {
          return projectLineItem.get('paymentFinalAmount') < projectLineItem.get('amount');
        }
      });
    }
  ),

  /**
   * Whether to display our base checkbox or not
   */
  _showBaseCheckbox: notEmpty('_expenseProjectLineItems'),

  /**
   * Placeholder for the base amount field
   */
  _basePlaceholder: computed(
    '_showBaseCheckbox',
    function () {
      if (this.get('_showBaseCheckbox')) {
        return 'Other';
      }
      return 'Description';
    }
  ),

  _subTitle: computed(
    'state.currentUser.isBuyer',
    function () {
      if (this.get('state.currentUser.isBuyer')) {
        return 'What are you advancing?';
      }
      return 'What would you like to have advanced?';
    }
  ),

  actions: {
    onItemSelect(projectLineItemId) {
      if (this.get('state.advanceProjectLineItemIds').includes(projectLineItemId)) {
        this.get('state.advanceProjectLineItemIds').removeObject(projectLineItemId);
      } else {
        this.get('state.advanceProjectLineItemIds').pushObject(projectLineItemId);
      }

      this.get('handleRefresh')();
    },

    toggleBaseAmount() {
      this.toggleProperty(`state.advanceBaseSelected`);

      this.set(`errors.base-amount`, undefined);

      this._updateBaseAmount.perform();
    },

    baseAmountChanged(amount) {
      this.stateManager.handleBaseAmountChange(amount);

      this._updateBaseAmount.perform();
    },

    baseDescriptionChanged(value) {
      this.stateManager.handleBaseDescriptionChange(value);

      this._updateBaseAmount.perform();
    },
  }
});
